import React, { useCallback, useEffect, useState } from 'react';
import { Select, useToast } from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import AdminPageShell from '../../../components/layout/AdminPageShell';
import PurchaseInvoicesTable from '../../../components/PurchaseInvoice/PurchaseInvoicesTable.js';
import PurchaseInvoiceDetailModal from '../../../components/PurchaseInvoice/PurchaseInvoiceDetailModal.js';
import { columnsDataPurchaseInvoices } from './variables/columnsDataPurchaseInvoices.js';
import { getData } from '../../../utils/httpClient.js';
import { useSearch } from '../../../contexts/SearchContext.js';
import { useResetPageOnFilterChange } from '../../../hooks/useResetPageOnFilterChange.js';
import { useTableSorting } from '../../../hooks/useTableSorting.js';

export default function PurchaseInvoices() {
  const { pathname } = useLocation();
  const { searchQuery, resetListFilters, registerExtraFilterClear } = useSearch();
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const toast = useToast();
  const [detailInvoiceId, setDetailInvoiceId] = useState(null);
  const [currencyFilter, setCurrencyFilter] = useState('');
  const { sorting, setSorting, sortBy, sortDir } = useTableSorting();

  useResetPageOnFilterChange(setPage, page, [
    searchQuery,
    rowsPerPage,
    sortBy,
    sortDir,
    currencyFilter,
  ]);

  const loadData = useCallback(
    async (query, pageOverride, currencyCode) => {
      const pageIndex = pageOverride ?? page;
      const currency = currencyCode ?? currencyFilter;
      try {
        const params = new URLSearchParams({
          page: String(pageIndex + 1),
          limit: String(rowsPerPage),
        });
        const term = (query ?? '').trim();
        if (term) {
          params.set('data', term);
        }
        if (currency) {
          params.set('currencyCode', currency);
        }
        if (sortBy) {
          params.set('sortBy', sortBy);
          params.set('sortDir', sortDir);
        }
        const response = await getData(`/provider-invoices?${params.toString()}`);
        const invoices = response?.payload?.invoices || [];
        const total = response?.payload?.total ?? invoices.length;
        setData(invoices);
        setTotalPages(Math.max(1, Math.ceil(total / rowsPerPage)));
      } catch (error) {
        setData([]);
        setTotalPages(1);
        const description =
          error?.message ||
          'No se pudo obtener el listado. Si el error persiste, verifique el permiso providers.view.';
        toast({
          title: 'Error al cargar facturas de compra',
          description,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [page, rowsPerPage, sortBy, sortDir, currencyFilter, toast],
  );

  useEffect(() => {
    return registerExtraFilterClear(() => setCurrencyFilter(''));
  }, [registerExtraFilterClear]);

  // Al entrar a la pantalla: limpiar búsqueda heredada y listar sin filtro.
  useEffect(() => {
    resetListFilters();
    setCurrencyFilter('');
    setPage(0);
    loadData('', 0, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    loadData(searchQuery);
  }, [page, searchQuery, rowsPerPage, sortBy, sortDir, currencyFilter, loadData]);

  return (
    <AdminPageShell
      title="Facturas de compra"
      actions={
        <Select
          size="sm"
          maxW="200px"
          bg="white"
          value={currencyFilter}
          onChange={(e) => setCurrencyFilter(e.target.value)}
          aria-label="Filtrar por moneda"
        >
          <option value="">Todas las monedas</option>
          <option value="UYU">Pesos (UYU)</option>
          <option value="USD">Dólares (USD)</option>
        </Select>
      }
    >
      <PurchaseInvoicesTable
        columnsData={columnsDataPurchaseInvoices}
        data={data}
        page={page}
        setPage={setPage}
        totalPages={totalPages}
        pageSize={rowsPerPage}
        setPageSize={setRowsPerPage}
        sorting={sorting}
        onSortingChange={setSorting}
        onInvoiceClick={(invoiceId) => {
          if (invoiceId) setDetailInvoiceId(invoiceId);
        }}
      />

      <PurchaseInvoiceDetailModal
        isOpen={Boolean(detailInvoiceId)}
        onClose={() => setDetailInvoiceId(null)}
        invoiceId={detailInvoiceId}
      />
    </AdminPageShell>
  );
}
